import { FC, useEffect, useRef, useState } from 'react';
import { DatePicker, InputNumber, Input, Select, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { API_DATE_FORMAT, DATE_FORMAT } from '@/constants';
import type { EditableCellProps, EditableValue } from './EditableCell.types';
import { CellButton, CellText, EditingCell } from './EditableCell.styles';

/** Nothing to show, in the same glyph the rest of the table uses. */
const EMPTY = '—';

/**
 * One line-item field, edited in place.
 *
 * Committing on blur or Enter and abandoning on Escape, which is what a
 * spreadsheet does and therefore what this table is read as. A value that did
 * not change saves nothing: every commercial field triggers a full margin and
 * booking recalculation on the server, so a cell merely tabbed through must
 * not queue one.
 *
 * The field stays in its editing state while the save is in flight, rather
 * than reverting to text and flicking back when the recalculated figure
 * arrives. Several of these cells feed each other — a rate change moves margin,
 * gross and net — and showing a stale neighbour as settled reads as a saved
 * value that is wrong.
 */
const EditableCell: FC<EditableCellProps> = ({
  value,
  type,
  options,
  disabled,
  disabledReason,
  minDate,
  maxDate,
  format,
  onSave
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditableValue>(value);
  const [saving, setSaving] = useState(false);
  /* Tracks whether this cell is still mounted when the save resolves: the
     table re-renders on invalidation, and setting state on a cell that has
     gone warns and leaks. */
  const live = useRef(true);
  /* Set on every mount, not only initialised: React mounts components twice in
     development, and a ref that only ever went false after the first unmount
     left each cell believing it was gone — stuck "saving" after its first edit. */
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  /* Escape closes an open calendar wherever focus is. Paging months moves focus
     onto the calendar's own buttons, out of reach of the input's key handler,
     which left Escape doing nothing. */
  useEffect(() => {
    if (!editing || type !== 'date') return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDraft(value);
        setEditing(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editing, type, value]);

  const commit = async () => {
    if (!editing) return;
    setEditing(false);

    const unchanged =
      draft === value ||
      (draft == null && value == null) ||
      (type === 'date' && dayjs(draft as string).isSame(dayjs(value as string), 'day'));
    if (unchanged) return;

    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      if (live.current) setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  /* Dates save the moment a day is picked, like a select. They cannot commit
     on blur: picking a day in the calendar blurs the input first, so the
     unchanged value was committed and the cell closed before the pick landed —
     which is why a date could never be changed here. Sent as YYYY-MM-DD, as
     the line item form sends it: a full timestamp of local midnight falls on
     the previous day in UTC during British Summer Time. */
  const saveDate = (next: Dayjs | null) => {
    setEditing(false);
    if (!next || (value && next.isSame(dayjs(value as string), 'day'))) return;
    setDraft(next.format(API_DATE_FORMAT));
    setSaving(true);
    Promise.resolve(onSave(next.format(API_DATE_FORMAT))).finally(() => {
      if (live.current) setSaving(false);
    });
  };

  if (disabled) {
    const text = <CellText $muted>{format ? format(value) : (value ?? EMPTY)}</CellText>;
    return disabledReason ? <Tooltip title={disabledReason}>{text}</Tooltip> : text;
  }

  if (!editing && !saving) {
    return (
      <CellButton type="button" onClick={() => setEditing(true)} title="Click to edit">
        {format ? format(value) : ((value as string) ?? EMPTY)}
      </CellButton>
    );
  }

  const shared = {
    size: 'small' as const,
    autoFocus: true,
    disabled: saving,
    onBlur: commit,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') cancel();
      if (event.key === 'Enter') commit();
    }
  };

  return (
    <EditingCell>
      {type === 'number' && (
        <InputNumber
          {...shared}
          value={draft as number}
          onChange={(next) => setDraft(next)}
          style={{ width: '100%' }}
        />
      )}
      {type === 'text' && (
        <Input
          {...shared}
          value={(draft as string) ?? ''}
          onChange={(event) => setDraft(event.target.value)}
        />
      )}
      {type === 'select' && (
        <Select
          {...shared}
          value={(draft as string) ?? undefined}
          options={options}
          showSearch
          optionFilterProp="label"
          onChange={(next) => setDraft(next)}
          /* Committed from the dropdown rather than on blur: picking an option
             blurs the field, and the two racing left the previous value saved. */
          onSelect={(next) => {
            setDraft(next);
            setEditing(false);
            if (next !== value) {
              setSaving(true);
              Promise.resolve(onSave(next)).finally(() => {
                if (live.current) setSaving(false);
              });
            }
          }}
          style={{ width: '100%' }}
          getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
        />
      )}
      {type === 'date' && (
        <DatePicker
          size="small"
          autoFocus
          /* Open on the click that started editing: the calendar is the editor,
             and a bare text box gave no sign a date could be picked. */
          open={!saving}
          disabled={saving}
          value={draft ? dayjs(draft as string) : null}
          format={DATE_FORMAT}
          /* A line item always has a flight, so there is nothing to clear to. */
          allowClear={false}
          disabledDate={(day) =>
            (minDate != null && day.isBefore(dayjs(minDate as string), 'day')) ||
            (maxDate != null && day.isAfter(dayjs(maxDate as string), 'day'))
          }
          onChange={saveDate}
          /* Closing without a pick — clicking away, Escape — leaves the date as it was. */
          onOpenChange={(open) => {
            if (!open) cancel();
          }}
          style={{ width: '100%', minWidth: 120 }}
        />
      )}
    </EditingCell>
  );
};

export default EditableCell;
